// Global reusable search params utilities and custom parsers
import { createParser } from "nuqs"
import z from "zod"
import type { TAllergen, TDishDiet } from "../api/maul-client/schemas/shared"
import { allergenSchema, dishDietSchema } from "../api/maul-client/schemas/shared"

// ============================================================================
// CUSTOM PARSERS - Reusable across the application
// ============================================================================

/**
 * Parser for Active/Inactive status
 * URL: ?st=active -> State: "Active"
 */
export const parseAsStatus = createParser({
  parse: (value) => {
    if (value === "active") return "Active"
    if (value === "inactive") return "Inactive"
    if (value === "all") return "all"
    return null
  },
  serialize: (value) => {
    if (value === "Active") return "active"
    if (value === "Inactive") return "inactive"
    return "all"
  },
})

/**
 * Parser for sort direction (asc/desc)
 */
export const parseAsSortDirection = createParser({
  parse: (value) => {
    if (value === "asc") return "asc"
    if (value === "desc") return "desc"
    return null
  },
  serialize: (value) => value,
})

/**
 * Parser for drawer mode (create/edit)
 * URL: ?mode=create -> State: "create"
 */
export const parseAsDrawerMode = createParser({
  parse: (value) => {
    if (value === "create") return "create"
    if (value === "edit") return "edit"
    if (value === "view") return "view"
    return null
  },
  serialize: (value) => value || "",
})

/**
 * Parser for allergens array
 * URL: ?al=gluten,nuts,eggs -> State: ["gluten", "nuts", "eggs"]
 */
export const parseAsAllergens = createParser<Array<TAllergen>>({
  parse: (value): Array<TAllergen> => {
    if (value === "") return []
    // Drop values that aren't valid allergens instead of trusting the URL
    return value
      .split(",")
      .map((candidate) => allergenSchema.safeParse(candidate))
      .filter((result) => result.success)
      .map((result) => result.data)
  },
  serialize: (value) => (value.length > 0 ? value.join(",") : ""),
  eq: (a, b) => a.length === b.length && a.every((v, i) => v === b[i]),
})

/**
 * Parser for diet types array
 * URL: ?dt=vegan,vegetarian -> State: ["vegan", "vegetarian"]
 */
export const parseAsDietTypes = createParser<Array<TDishDiet>>({
  parse: (value): Array<TDishDiet> => {
    if (value === "") return []
    // Drop values that aren't valid diet types instead of trusting the URL
    return value
      .split(",")
      .map((candidate) => dishDietSchema.safeParse(candidate))
      .filter((result) => result.success)
      .map((result) => result.data)
  },
  serialize: (value) => (value.length > 0 ? value.join(",") : ""),
  eq: (a, b) => a.length === b.length && a.every((v, i) => v === b[i]),
})

/**
 * Parser for temperature filter
 * URL: ?tmp=hot -> State: "HOT"
 */
export const parseAsTemperature = createParser({
  parse: (value) => {
    if (value === "hot") return "HOT"
    if (value === "cold") return "COLD"
    if (value === "all") return "all"
    return null
  },
  serialize: (value) => {
    if (value === "HOT") return "hot"
    if (value === "COLD") return "cold"
    return "all"
  },
})

/**
 * Parser for prep confirmation flow dev state override.
 * Walks the two-button flow from "both disabled" to "both confirmed".
 *
 *   ?ps=idle       -> both buttons disabled
 *   ?ps=active     -> row 1 enabled, row 2 disabled
 *   ?ps=confirmed  -> row 1 confirmed (checkmark), row 2 enabled
 *   ?ps=finished   -> both confirmed (checkmarks)
 *
 * Absent param -> no override; real time-rule + sessionStorage logic applies.
 */
export const parseAsPrepState = createParser({
  parse: (value) => {
    if (value === "idle") return "idle"
    if (value === "active") return "active"
    if (value === "confirmed") return "confirmed"
    if (value === "finished") return "finished"
    return null
  },
  serialize: (value) => value || "",
})

/**
 * Parser for secondary market fflag
 * URL: ?sm=true -> State: true
 */
export const parseAsEnableSecondaryMarket = createParser({
  parse: (value) => {
    if (value === "true") return true
    if (value === "false") return false
    return null
  },
  serialize: (value) => (value ? "true" : "false"),
})

/**
 * Rolling feedback-window periods (in days) for restaurant insights.
 * URL: ?period=180 -> State: 180
 */
export const FEEDBACK_PERIOD_DAYS = [30, 180, 365] as const
export type TFeedbackPeriodDays = (typeof FEEDBACK_PERIOD_DAYS)[number]

/**
 * Default insights window: 6 months, so the monthly like/dislike chart opens
 * with a visible trend (Asana 1217576621011877). The codec default and the
 * period dropdown both read this — keep it the single source of truth.
 */
export const DEFAULT_FEEDBACK_PERIOD_DAYS: TFeedbackPeriodDays = 180

/** A specific calendar-month window, `yyyy-MM` (e.g. `?period=2026-05`). */
export type TFeedbackMonthKey = string
const FEEDBACK_MONTH_KEY = /^\d{4}-(0[1-9]|1[0-2])$/

/** The `?period=` value: a rolling window in days, or one specific month. */
export type TFeedbackWindowValue = TFeedbackPeriodDays | TFeedbackMonthKey

export const parseAsFeedbackPeriod = createParser<TFeedbackWindowValue>({
  parse: (value) => {
    const days = FEEDBACK_PERIOD_DAYS.find((candidate) => String(candidate) === value)
    if (days !== undefined) return days
    return FEEDBACK_MONTH_KEY.test(value) ? value : null
  },
  serialize: (value) => String(value),
})

/**
 * One month focused inside a rolling window by clicking its chart bar,
 * `yyyy-MM`; several are comma-joined (e.g. `?month=2026-05,2026-07`). Unlike
 * `?period=2026-05` it narrows what the loader already fetched rather than
 * refetching a one-month window.
 */
export const parseAsFeedbackMonthKey = createParser<TFeedbackMonthKey>({
  parse: (value) => (FEEDBACK_MONTH_KEY.test(value) ? value : null),
  serialize: (value) => value,
})

export const forcedAppTimeSchema = z.iso.datetime()
export const parseAsOptionalTimeString = createParser({
  parse: (value: string): string | null => {
    if (value === "") return null
    const parseResult = forcedAppTimeSchema.safeParse(value)
    const res = parseResult.success ? parseResult.data : null

    return res
  },
  serialize: (value: string | null): string => {
    if (value === null) {
      return ""
    }

    const result = forcedAppTimeSchema.safeParse(value)
    if (!result.success) {
      throw new Error(`Invalid forced app time: ${value}`)
    }

    const res = result.data
    return res
  },
})
